import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import styles from './Input.scss';

export const Input = ({
  reset,
  setReset,
  suggestions = [],
  placeholder = '',
  type = 'text',
  icon,
  value,
  errorText,
  invalid,
  name,
  textarea = false,
  className,
  onChange,
  tableCellStyle,
  readonly = false,
  ...props
}) => {
  const [suggestionsList, setSuggestionsList] = useState(suggestions);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (!value && suggestionsList.length !== suggestions.length) {
      handleChange({ target: { value } });
    } else {
      filterSuggestions(value);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOrFocusOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target) && document.activeElement !== inputRef.current) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOrFocusOutside);
      document.addEventListener('focusin', handleClickOrFocusOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOrFocusOutside);
      document.removeEventListener('focusin', handleClickOrFocusOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOrFocusOutside);
      document.removeEventListener('focusin', handleClickOrFocusOutside);
    };
  }, [isOpen]);

  const openSuggestionsList = () => {
    setIsOpen(true);
  };

  const filterSuggestions = (value) => {
    if (suggestions.length) {
      const filtered = suggestions.filter(
        (suggestion) => suggestion.toLowerCase().includes(value?.toLowerCase()) && suggestion !== value
      );
      const sorted = filtered.sort((a, b) => a.localeCompare(b));
      setSuggestionsList(sorted);
    }
  };

  const handleChange = ({ target }) => {
    const { value } = target;
    onChange({ target });
    filterSuggestions(value);
  };

  const handleSelectSuggestion = (suggestion) => {
    onChange({ target: { name, value: suggestion } });
    setSuggestionsList([]);
    setIsOpen(false);
  };

  return tableCellStyle ? (
    <label
      className={cn(styles.field, className, {
        [styles.invalid]: invalid,
        [styles.tableCell]: tableCellStyle,
        [styles.readonly]: readonly,
      })}
      ref={inputRef}
    >
      <input
        className={cn(styles.input, { [styles.expanded]: suggestionsList.length && isOpen })}
        name={name}
        type={type}
        placeholder={placeholder}
        value={value}
        onFocus={openSuggestionsList}
        onChange={handleChange}
        autoComplete='off'
        readOnly={readonly}
        {...props}
      />
      {suggestions.length > 0 && (
        <ul
          className={cn(styles.list, {
            [styles.open]: isOpen && suggestionsList.length > 0,
          })}
        >
          {suggestionsList.map((suggestion, index) => {
            const regex = new RegExp(`(${value})`, 'gi');
            const parts = suggestion?.split(regex);

            return (
              <li key={index}>
                <button
                  className={styles.listItem}
                  tabIndex={isOpen ? 0 : -1}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  type='button'
                >
                  {value
                    ? parts.map((part, i) => (regex.test(part) ? <strong key={i}>{part}</strong> : part))
                    : suggestion}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </label>
  ) : (
    <label
      className={cn(styles.field, className, {
        [styles.invalid]: invalid,
        [styles.detailError]: errorText,
        [styles.readonly]: readonly,
      })}
      ref={inputRef}
    >
      <div className={styles.inputArea}>
        {textarea ? (
          <textarea
            className={cn(styles.input, styles.textarea)}
            name={name}
            value={value}
            placeholder={placeholder}
            onChange={handleChange}
            readOnly={readonly}
            {...props}
          />
        ) : (
          <input
            className={cn(styles.input, { [styles.expanded]: suggestionsList.length && isOpen })}
            name={name}
            type={type}
            placeholder={placeholder}
            value={value}
            onFocus={openSuggestionsList}
            onChange={handleChange}
            autoComplete='off'
            readOnly={readonly}
            {...props}
          />
        )}
        {!textarea && icon && (
          <FontAwesomeIcon
            className={cn(styles.icon, styles.roleIcon, { [styles.filled]: String(value)?.length > 0 })}
            icon={icon}
          />
        )}
        <FontAwesomeIcon className={cn(styles.icon, styles.error, styles.errorIcon)} icon={faCircleExclamation} />
      </div>
      <span className={cn(styles.error, styles.errorText)}>{errorText}</span>
      {suggestions.length > 0 && (
        <ul
          className={cn(styles.list, {
            [styles.open]: isOpen && suggestionsList.length > 0,
          })}
        >
          {suggestionsList.map((suggestion, index) => {
            const regex = new RegExp(`(${value})`, 'gi');
            const parts = suggestion.split(regex);

            return (
              <li key={index}>
                <button
                  className={styles.listItem}
                  tabIndex={isOpen ? 0 : -1}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  type='button'
                >
                  {value
                    ? parts.map((part, i) => (regex.test(part) ? <strong key={i}>{part}</strong> : part))
                    : suggestion}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </label>
  );
};
